import { useState } from "react";
import { RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { AskReviewButton } from "./AskReviewButton";
import { PaidActionDialog } from "./PaidActionDialog";
import api, { errorText } from "../lib/api";
import { holdCost, isWaiting, usd } from "../lib/viewHold";

const form = (fields) => {
  const fd = new FormData();
  Object.entries(fields).forEach(([k, v]) => v && fd.append(k, v));
  return fd;
};

// Above the view cards while a broken view holds Start sculpting: the reasons, "Re-render the front" for a broken
// front (back to the preview step), and "Ask us to review it".
export const ViewHoldBanner = ({ creation, blocks, onUpdate, onBack }) => {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const cost = holdCost(creation.view_check, "render");

  const rerender = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/creations/${creation.id}/regenerate`, form({ style: creation.style || "classic", figure: creation.figure || "full" }));
      setAsking(false);
      onUpdate(data);
      onBack?.();
      toast.success("New front render ready. Approve it to make fresh views.");
    } catch (e) {
      toast.error(errorText(e, "Could not re-render the front. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="view-hold-banner" className="rounded-3xl border border-destructive/30 bg-destructive/5 p-5 space-y-3">
      <p className="font-heading text-lg font-bold flex items-center gap-2">
        <ShieldAlert className="h-5 w-5 text-destructive shrink-0" /> Start sculpting is on hold
      </p>
      <ul className="space-y-1 text-sm list-disc pl-5">
        {Object.entries(blocks).map(([v, b]) => (
          <li key={v} data-testid={`hold-reason-${v}`}>{b.reason}</li>
        ))}
      </ul>
      <p className="text-sm text-muted-foreground">
        {blocks.front ? "Re-render the front to start again from a new image" : "Redo the marked view below"}, or ask us to review it if it looks right to you.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        {blocks.front && (
          <Button data-testid="hold-rerender-front-btn" onClick={() => setAsking(true)} className="rounded-full h-11 gap-2 font-bold">
            <RefreshCw className="h-4 w-4" /> Re-render the front · about {usd(cost)}
          </Button>
        )}
        <AskReviewButton creation={creation} onUpdate={onUpdate} className="sm:flex-1" />
      </div>
      <PaidActionDialog
        open={asking}
        title="Re-render the front?"
        what="A new AI front render is made and the duck goes back to the preview step. Approving it makes fresh views."
        cost={cost}
        cancelsReview={isWaiting(creation)}
        confirmLabel="Re-render the front"
        busy={busy}
        onCancel={() => setAsking(false)}
        onConfirm={rerender}
      />
    </div>
  );
};
